import { Quality } from 'common';
import Data from 'data';
import { presetItems } from './itemTypes';

// find item containing specified index in inventory
export function inventoryFindItem(inventory, index, state, {columns}) {
  if (inventory[index]) return index;
  const y = Math.floor(index / columns);
  const x = index - y * columns;
  let result = -1;
  // odd usage of `some` because `findIndex` iterates over holes
  inventory.some((id, i) => {
    const base = Data.items[(state.items[id] || presetItems[id])?.base];
    if (!base) return false;
    const iy = Math.floor(i / columns);
    const ix = i - iy * columns;
    if (x >= ix && y >= iy && x < ix + base.invwidth && y < iy + base.invheight) {
      result = i;
      return true;
    }
    return false;
  });
  return result;
}

// find first item intersecting another item if placed at specified index
export function inventoryFindIntersection(inventory, index, id, state, {columns}) {
  const item = state.items[id] || presetItems[id];
  const base = item && Data.items[item.base];
  if (!base) return -1;
  const y = Math.floor(index / columns);
  const x = index - y * columns;
  const w = base.invwidth, h = base.invheight;
  let result = -1;
  // odd usage of `some` because `findIndex` iterates over holes
  inventory.some((id, i) => {
    const base = Data.items[(state.items[id] || presetItems[id])?.base];
    if (!base) return false;
    const iy = Math.floor(i / columns);
    const ix = i - iy * columns;
    if (ix + base.invwidth > x && iy + base.invheight > y && ix < x + w && iy < y + h) {
      result = i;
      return true;
    }
    return false;
  });
  return result;
}

// check if inventory contains items that a player can only carry one of
export function inventoryHasDuplicate(inventory, id, state, except) {
  const item = state.items[id] || presetItems[id];
  const base = Data.items[item?.base];
  if (!base) return false;

  return inventory.some((id, i) => {
    if (i === except) return false;
    const oitem = state.items[id] || presetItems[id];
    const obase = Data.items[oitem?.base];
    if (!obase) return false;

    if (base.type === 'ques' && obase === base) return true;
    if (item.quality === Quality.UNIQUE && item.unique === oitem.unique && Data.uniqueItems[item.unique]?.carry1) return true;
    return false;
  });
}

function findValidSpot(x, y, width, height, targets, {rows, columns}) {
  for (let sum = 0; sum <= width + height - 2; ++sum) {
    for (let dx = Math.max(-sum, -width + 1); dx <= 0; ++dx) {
      const tx = x + dx;
      const ty = y - dx - sum;
      if (tx < 0 || ty < 0 || tx + width > columns || ty + height > rows) continue;
      const index = ty * columns + tx;
      if (!targets[index]) return index;
    }
  }
  return -1;
}

// find nearest spot where an item can be placed
// if initial cell is occupied, returns occupying item index instead
export function inventoryFindNearest(inventory, index, id, state, storageSize) {
  const found = inventoryFindItem(inventory, index, state, storageSize);
  if (found >= 0) return found;
  const { rows, columns } = storageSize;

  const item = state.items[id] || presetItems[id];
  const base = item && Data.items[item.base];
  if (!base) return inventory; // use inventoryEquipItem function for that

  const width = base.invwidth, height = base.invheight;

  const targets = new Uint8Array(rows * columns);
  inventory.forEach((id, i) => {
    const base = Data.items[(state.items[id] || presetItems[id])?.base];
    if (!base) return;
    const iy = Math.floor(i / columns);
    const ix = i - iy * columns;
    const minx = Math.max(0, ix - width + 1);
    const maxx = Math.min(columns - 1, ix + base.invwidth - 1);
    const miny = Math.max(0, iy - height + 1);
    const maxy = Math.min(rows - 1, iy + base.invheight - 1);
    for (let y = miny; y <= maxy; ++y) {
      targets.fill(1, y * columns + minx, y * columns + maxx + 1);
    }
  });

  const y = Math.floor(index / columns);
  const x = index - y * columns;
  const spot = findValidSpot(x, y, width, height, targets, storageSize);
  return spot;
}

// equip an item in inventory, removing all intersecting items
export function inventoryEquipItem(inventory, index, id, state, {rows, columns}) {
  if (!id) {
    if (!inventory[index]) return inventory;
    inventory = inventory.slice();
    delete inventory[index];
    return inventory;
  }

  if (inventory[index] === id) return inventory;

  const item = state.items[id] || presetItems[id];
  const base = item && Data.items[item.base];
  if (!base) return inventory;

  const y = Math.floor(index / columns);
  const x = index - y * columns;
  const w = base.invwidth, h = base.invheight;
  if (x < 0 || x + w > columns || y < 0 || y + h > rows) return inventory;

  const result = [];
  inventory.forEach((id, i) => {
    const base = Data.items[(state.items[id] || presetItems[id])?.base];
    if (!base) return;
    const iy = Math.floor(i / columns);
    const ix = i - iy * columns;
    if (ix + base.invwidth <= x || iy + base.invheight <= y || ix >= x + w || iy >= y + h) {
      result[i] = id;
    }
  });
  result[index] = id;
  return result;
}
